class FormHandler {
  static #dateTimePicker = "datetime";
  static #postalCodeType = "postal_code";
  static #CanadaCountryCode = "CA";
  static #UnitedStateCountryCode = "US";
  static #MexicoCountryCode = "MX";
  /** @type {Function | null} */
  onBeforeValidFormSubmit;
  /** @type {Function | null} */
  onSuccess;
  /** @type {Function | null} */
  onError;
  /**
   * Optional custom sender. When set, submitForm() calls this instead of the built-in fetch, so the
   * form can be submitted through a typed API client (e.g. a DivZero client) while keeping all the
   * validation / UI / button handling. It receives the collected FormData and this FormHandler, and
   * must resolve with the result (passed to onSuccess) or throw to trigger the error path.
   * @type {((formData: FormData, formHandler: FormHandler) => Promise<any>) | null}
   */
  submitHandler;

  /**
   *
   * @param {String | null} url  Endpoint for the built-in fetch; may be null when using submitHandler.
   * @param {HTMLElement} formElement
   * @param {String | null} queryParams
   * @param {*} headers
   * @param {String | null} countryCode
   */
  constructor(url, formElement, queryParams = null, headers = null, countryCode = null) {
    if (!(formElement instanceof HTMLFormElement)) {
      throw new Error("formElement must be a valid HTMLFormElement");
    }

    this.url = url;
    this.formElement = formElement;
    this.queryParams = queryParams ?? {};
    this.countryCode = countryCode;

    this.headers = new Headers();

    //Public front-end credentials (only used by the built-in fetch; a submitHandler brings its own auth)
    if (url && (url.startsWith("https://divzero") || url.startsWith("https://localhost:7084"))) {
      this.headers.append(
        "Authorization",
        "Basic c055aUdGOGhnQnB1SGVydE10dEY6MUp1SkdGNnZQM2ViWFdqam1WeU0="
      );
    }
    else {
      this.headers.append(
        "Authorization",
        "Basic TVZobGVsa3FScDk0TlI2NTFseXM6c2ZIUnpVT1FGcEcxclJ6WGx1bE0="
      );
    }



    //Adding the custom headers for this form
    if (headers != null) {
      for (const [key, value] of Object.entries(headers)) {
        this.headers.append(key, value);
      }
    }

    this.onBeforeValidFormSubmit = null;
    this.onSuccess = null;
    this.onError = null;
    this.submitHandler = null;

    //Add `novalidate` to prevent native validation blocking the custom submit logic
    this.formElement.setAttribute("novalidate", "");

    //Saving initial values to be able to reset to them later
    this.initialValues = this._getFormSnapshot();

    this._attachEventListeners();

    if (countryCode == FormHandler.#CanadaCountryCode) {
      const fields = this.getAllFormElements();
      fields.filter(/** @returns {field is HTMLInputElement} */(field) => field.dataset.customType == FormHandler.#postalCodeType).forEach(postalCodeField => {
        postalCodeField.maxLength = 7;
        postalCodeField.addEventListener('input', (e) => {
          const oldPosition = postalCodeField.selectionStart ?? 0; // Get cursor position before change

          //Remove non-alphanumeric characters
          let value = postalCodeField.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

          //Add space after the 3rd character if needed
          if (value.length > 2 && value[3] !== ' ') {
            value = value.slice(0, 3) + ' ' + value.slice(3);
          }

          const oldLength = postalCodeField.value.length;
          postalCodeField.value = value;
          const newLength = postalCodeField.value.length;
          let newPosition = oldPosition;

          //Adjust cursor position if space was added
          if (oldLength <= 3 && newLength > 3) newPosition = oldPosition + 1;

          //Restore the cursor to the appropriate position
          postalCodeField.setSelectionRange(newPosition, newPosition);
        });
        postalCodeField.addEventListener('keydown', (e) => {
          if (e.key === 'Backspace') {
            const position = postalCodeField.selectionStart;

            // If trying to delete the space, move the cursor before the space
            if (position === 4) {
              e.preventDefault();
              postalCodeField.setSelectionRange(3, 3);
            }
          }
        });
      });
    }
  }

  _attachEventListeners() {
    this.formElement.addEventListener("submit", (e) => {
      e.preventDefault();
      this.submitForm(e);
    });
  }

  isValid(showErrorsToUser = true) {
    let valid = true;

    if (showErrorsToUser) {
      // Clear existing styles
      this.formElement
        .querySelectorAll("input, select, textarea")
        .forEach((el) => {
          el.classList.remove("invalid-form-value");
        });
    }

    const invalidFields = [];
    const fields = this.getAllFormElements();
    for (const field of fields) {
      if (!this.isFieldValid(field)) {
        valid = false;

        if (showErrorsToUser) {
          /** @type {HTMLElement} */
          let elementToShowError = field;

          //If the element is hidden then use the parent to show the error
          elementToShowError = this._getElementToAffect(field);

          invalidFields.push({
            field: field,
            elementToShowError: elementToShowError,
          });

          elementToShowError.classList.add("invalid-form-value");

          const isDatetime = field.dataset.customType == FormHandler.#dateTimePicker;
          //Add a one time listener to remove the error style
          const eventType = field.tagName === "SELECT" ? "change" : (isDatetime ? tempusDominus.Namespace.events.change : "input");

          if (isDatetime) {
            const clearStyle = () => {
              elementToShowError.classList.remove("invalid-form-value");
            };
            elementToShowError.closest('.royer-date-time-picker')?.addEventListener(eventType, clearStyle, { once: true });
          }
          else if (field.type === "radio") {
            const radioGroup = document.querySelectorAll(`input[type="radio"][name="${field.name}"]`);
            radioGroup
            const clearStyle = () => {
              radioGroup.forEach(radio => {
                let radioErrorElement = radio;
                //If the element is hidden then use the parent to show the error
                radioErrorElement = this._getElementToAffect(field);

                radioErrorElement.classList.remove("invalid-form-value");
              });
            };
            radioGroup.forEach(radio => {
              radio.addEventListener("change", clearStyle, { once: true });
            });
          }
          else if (field.type === "checkbox") {
            const checkboxGroup = /** @type {NodeListOf<HTMLInputElement>}*/ (document.querySelectorAll(`input[type="checkbox"][name="${field.name}"]`));
            if (checkboxGroup.length > 1) {
              const clearStyle = () => {
                checkboxGroup.forEach(cb => {
                  this._getElementToAffect(cb).classList.remove("invalid-form-value");
                });
              };
              checkboxGroup.forEach(cb => {
                cb.addEventListener("change", clearStyle, { once: true });
              });
            } else {
              const clearStyle = () => {
                elementToShowError.classList.remove("invalid-form-value");
              };
              elementToShowError.addEventListener(eventType, clearStyle, { once: true });
            }
          }
          else {
            const clearStyle = () => {
              elementToShowError.classList.remove("invalid-form-value");
            };
            elementToShowError.addEventListener(eventType, clearStyle, { once: true });
          }
        }
      }
    }

    const recaptchaV2ResponseField = /** @type {HTMLTextAreaElement | null}*/ (this.formElement.querySelector("textarea[name='g-recaptcha-response']"));
    //If there is a google recaptcha V2 present on the form we want to validate it too (at least check if there is a value, the actual true validation is done in the back-end)
    if (recaptchaV2ResponseField) {
      if (!recaptchaV2ResponseField.value) {
        valid = false;

        if (showErrorsToUser) {
          const elementToShowError = recaptchaV2ResponseField.closest(".g-recaptcha")?.querySelector("div");
          elementToShowError?.classList.add("invalid-form-value");

          invalidFields.push({
            field: recaptchaV2ResponseField,
            elementToShowError: elementToShowError,
          });

          //Add a one-time polling function to remove the error style (we have to do it this way because the iframe doesn't trigger any event when changing the textarea)
          let lastValue = recaptchaV2ResponseField.value;

          const intervalId = setInterval(() => {
            if (recaptchaV2ResponseField.value !== lastValue) {
              lastValue = recaptchaV2ResponseField.value;
              elementToShowError?.classList.remove("invalid-form-value");
              clearInterval(intervalId);
            }
          }, 200);
        }
      }
    }

    if (!valid && showErrorsToUser) {
      const highestField = invalidFields.reduce((topMost, current) => {
        return (current.elementToShowError?.getBoundingClientRect().top ?? 0) <
          (topMost.elementToShowError?.getBoundingClientRect().top ?? 0)
          ? current
          : topMost;
      });

      highestField.elementToShowError?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      highestField.field.focus();
    }

    return valid;
  }

  /**
   * 
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field 
   * @returns 
   */
  isFieldValid(field) {
    let isValid = true;

    if (field.classList.contains("form-ignore")) return isValid;

    //We check if it's still valid for each check because if it's invalid then we don't need to continue to check the other validation rules

    //If the field is required check if it's empty
    if ((isValid && field.required) || (isValid && field.hasAttribute('required-if-visible') && !field.classList.contains('d-none'))) {
      if (field.type === "checkbox") {
        //If it's a checkbox group and required then it's at least one not all of them that are required
        const checkboxGroup = /** @type {NodeListOf<HTMLInputElement>}*/ (document.querySelectorAll(`input[type="checkbox"][name="${field.name}"]`));
        if (checkboxGroup.length > 1)
          isValid = Array.from(checkboxGroup).some(cb => cb.checked);
        else if (field instanceof HTMLInputElement)
          isValid = field.checked;
        else
          isValid = false;
      }
      else if (field.type === "radio")
        isValid = /** @type {HTMLInputElement[]}*/ (Array.from(document.querySelectorAll(`input[type="radio"][name="${field.name}"]`))).some(input => input.checked);
      else
        isValid = field.value != null && field.value.trim() != "";
    }
    //If field is an email type check the format
    if (isValid && field.type == "email" && !(new RegExp(/[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/i).test(field.value.trim()))) {
      isValid = false;
    }
    //If field is a phone type check the format (loosely checking, the exact valid format is way too complicated)
    else if (isValid && field.type == "tel" && !(new RegExp(/^\+?[0-9\s\-().]{7,}(?:\s?(?:ext|x|extension|#|\*)\s?\d{1,5})?$/i).test(field.value.trim()))) {
      isValid = false;
    }
    //If fiels is a postal code type and the form is for canada check the postal code format for canada
    else if (isValid && field.dataset.customType == FormHandler.#postalCodeType && this.countryCode == FormHandler.#CanadaCountryCode && !(new RegExp(/^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/).test(field.value.trim()))) {
      isValid = false;
    }
    //If it's a date time input and the value is not empty check if it can be converted to datetime to make sure it's valid format
    else if (isValid && field.dataset.customType == FormHandler.#dateTimePicker && field.value != '') {
      isValid = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}( (0[1-9]|1[0-2]):\d{2} (AM|PM))?$/.test(field.value.trim());
    }

    return isValid;
  }

  getAllFormElements() {
    return /** @type {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]}*/(Array.from(this.formElement.elements)).filter(tag => ["select", "textarea", "input"].includes(tag.tagName.toLowerCase()));
  }

  getFormData() {
    const fd = new FormData(this.formElement);
    //Removing field specified as ignored (used for conditional inputs)
    /** @type {NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>}*/ (this.formElement.querySelectorAll(".form-ignore, .form-ignore-on-submit")).forEach(el => {
      if (el.name) fd.delete(el.name);
    });
    return fd;
  }

  getFullUrl() {
    const query = new URLSearchParams(this.queryParams).toString();
    const url = this.url ?? "";
    return query ? `${url}?${query}` : url;
  }

  /**
   * 
   * @param {SubmitEvent} event 
   * @returns 
   */
  async submitForm(event) {
    if (!this.isValid(true)) {
      return;
    }

    //Disable the button while sending to prevent spam
    if (event.submitter instanceof HTMLButtonElement) {
      event.submitter.disabled = true;
      event.submitter.classList.add("form-button-loading");
    }

    let formData = this.getFormData();
    if (typeof this.onBeforeValidFormSubmit === "function") {
      const result = this.onBeforeValidFormSubmit(formData);
      //Function might not return an updated form data so we check if it is first
      if (result instanceof FormData) {
        formData = result;
      }
    }

    try {
      /** @type {any} */
      let data;
      /** @type {Response | null} */
      let response = null;

      if (typeof this.submitHandler === "function") {
        //Delegate the actual send (e.g. a typed API client call). It resolves with the result that
        //gets passed to onSuccess, or throws to hit the catch below.
        data = await this.submitHandler(formData, this);
      }
      else {
        if (!this.url) throw new Error("FormHandler has no url and no submitHandler to submit with");

        /** @type {RequestInit} */
        const requestOptions = {
          method: "POST",
          headers: this.headers,
          body: formData,
          redirect: "follow",
          mode: "cors",
          cache: "no-cache",
        };

        response = await fetch(this.getFullUrl(), requestOptions);

        if (!response.ok) throw new Error(`HTTP error ${response.status}`);

        const contentType = response.headers.get("Content-Type");
        const isJson = contentType && contentType.includes("application/json");
        data = isJson ? await response.json() : await response.text();
      }

      this.formElement.classList.add("form-sent");

      this.disableAllInputs();

      this.formElement.querySelector("form-submit-btn")?.classList.add("d-none");
      this.formElement.querySelector("form-reset-btn")?.classList.remove("d-none");
      this.formElement.querySelector(".form-sent-thank-you-wrapper")?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      if (typeof this.onSuccess === "function") {
        this.onSuccess(data, response);
      }
    } catch (error) {
      this.formElement.classList.add("form-error");
      this.formElement.querySelector(".form-error-wrapper")?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      this.disableAllInputs();

      if (typeof this.onError === "function") {
        this.onError(error);
      } else {
        console.error("Form submission error:", error);
      }
    } finally {
      if (event.submitter instanceof HTMLButtonElement) {
        //Enable the button after receiving the response or having an error
        event.submitter.disabled = false;
        event.submitter.classList.remove("form-button-loading");
      }
    }
  }

  resetForm() {
    // Reset text inputs, checkboxes, radios, selects, etc. to the initial values snapshotted from the constructor
    for (const [name, value] of Object.entries(this.initialValues)) {
      const elements = this.formElement.elements.namedItem(name);
      if (!elements) continue;
      if (elements instanceof RadioNodeList) {
        [...elements].forEach((el) => {
          if (el.type === "checkbox" || el.type === "radio") {
            el.checked = value.includes(el.value);
          } else {
            el.value = value[0];
          }
          el.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      else {
        const el = elements;
        if (el instanceof HTMLSelectElement) {
          const optionToSelect = Array.from(el.options).find(opt => opt.value === value[0]);
          if (optionToSelect) optionToSelect.selected = true;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        else if (el instanceof HTMLInputElement) {
          if (el.type === "checkbox" || el.type === "radio") {
            el.checked = value.includes(el.value);
          } else {
            el.value = value[0];
          }
        }
        else {
          /** @type {HTMLTextAreaElement} */ (el).value = value[0];
        }
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    //Reset file inputs
    for (const el of /** @type {NodeListOf<HTMLInputElement>}*/ (this.formElement.querySelectorAll("input[type='file']"))) {
      el.value = "";
    }

    this.formElement.classList.remove("form-sent");
    this.formElement.classList.remove("form-error");

    this.enableAllInput();

    this.formElement.querySelector(".form-submit-btn")?.classList.remove("d-none");
    this.formElement.querySelector(".form-reset-btn")?.classList.add("d-none");
  }

  //Get a snapshot of the current values in the form. Main use is to be able to reset the form to the initial values later
  _getFormSnapshot() {
    /** @type {Record<string, string[]>} */
    const snapshot = {};
    for (const element of this.formElement.elements) {
      if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement)) continue;
      if (!element.name) continue;

      if (element instanceof HTMLInputElement && (element.type === "checkbox" || element.type === "radio")) {
        if (!snapshot[element.name]) snapshot[element.name] = [];
        if (element.checked) snapshot[element.name].push(element.value);
      } else {
        snapshot[element.name] = [element.value];
      }
    }
    return snapshot;
  }

  /**
   * Use this to affect the actual visible element representing the input (if an input is hidden it will affect the parent instead)
   * @param {HTMLElement} el 
   * @returns 
   */
  _getElementToAffect(el) {
    //If the element is hidden then use the parent
    if (window.getComputedStyle(el).display === "none") return el.parentElement ?? el;
    else return el;
  }

  /**
   * 
   * @param {HTMLInputElement} el 
   */
  disableInput(el) {
    const elToAffect = this._getElementToAffect(el);
    this.disabledPropertyIfPossible(elToAffect);
    elToAffect.classList.add("disabled");
  }

  disableAllInputs() {
    this.getAllFormElements().map(el => this._getElementToAffect(el)).forEach(elToAffect => {
      this.disabledPropertyIfPossible(elToAffect);
      elToAffect.classList.add("disabled");
    });
  }

  /**
   * 
   * @param {HTMLInputElement} el 
   */
  enableInput(el) {
    const elToAffect = this._getElementToAffect(el);
    this.enablePropertyIfPossible(elToAffect);
    elToAffect.classList.remove("disabled");
  }

  enableAllInput() {
    this.getAllFormElements().map(el => this._getElementToAffect(el)).forEach(elToAffect => {
      this.enablePropertyIfPossible(elToAffect);
      elToAffect.classList.remove("disabled");
    });
  }

/**
 * 
 * @param {HTMLElement} el 
 * @returns True if the element had a disable property that was successfully put to true, false if the element doesn't have a disable property
 */
  disabledPropertyIfPossible(el) {
    if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement || el instanceof HTMLFieldSetElement) {
      el.disabled = true;
      return true;
    }
    else return false;
  }

  /**
 * 
 * @param {HTMLElement} el 
 * @returns True if the element had a disable property that was successfully put to false, false if the element doesn't have a disable property
 */
  enablePropertyIfPossible(el) {
    if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement || el instanceof HTMLFieldSetElement) {
      el.disabled = false;
      return true;
    }
    else return false;
  }
}
